import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link} from 'react-router-dom';
import { faArrowLeftLong, faChevronDown, faChevronRight, faCircleLeft, faDice, faDownload, faRobot, faUpload, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { SVGGenerationArrowStaticBlue } from '../../components/svgs';
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { stateIconError, stateIconWaiting } from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedImage } from '../../components/ui/AuthedImage';
import { ImageGernerationSimpleInputProps } from '../../types/imageSimple/imageGeneration';
import { aspectRatioList, getImagePixelSize, valueIsAspectRatioType } from '../../utils/images/imageConvertFormats';
import { getImageDimensions } from '../../utils/images/editFunctions';
import { ImageCreateProps, ImageGenerationBodyProps, qualityList, samplerList, schedulerList } from '../../types/ai/api';
import { ApiPayment } from '../../apis/ApiPayment';
import EffectThemeDropdown from '../../components/effectTheme/effectThemeDropdown';
import { renderStyleDropdownItem, styleFilterGroups, stylesList } from '../../utils/images/imageStylesList';
import { InfoPopoverButton } from '../../components/app/ImageStudio/InfoButton';
import { CfgInfoElement, QualityInfoElement, SamplerInfoElement, SamplingStepsInfoElement, SchedulerInfoElement, SeedInfoElement } from '../../utils/aiModels/infotexts';
import EffectThemeRange from '../../components/effectTheme/effectThemeRange';
import EffectThemeInput from '../../components/effectTheme/effectThemeInput';
import { Form } from 'react-bootstrap';
import { handleGetRandomNumber } from '../../utils/calculations';
import { styleDescriptionPrompt } from '../../utils/aiModels/prompts';

export const ImageToImageEdit = () => {
    const { t } = useTranslation('generation');
    const [firstImg,setFirstImg] = useState<{file:any,src:string}|null>(null);
    const [generateGif, setGenerateImgSecond] = useState(false);
    const [inputsCreate,setInputsCreate] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)'
    });
    const [inputsEdit, setInputsEdit] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)'
    });
    const [seedRandomEditActive, setSeedRandomEditActive] = useState(true);
    const [seedRandomCreateActive, setSeedRandomCreateActive] = useState(true);
    const [creationCostsCreate, setCreationCostsCreate] = useState<number|null>(0);
    const [creationCostsEdit, setCreationCostsEdit] = useState<number|null>(0);
    const [showSettingsCreate, setShowSettingsCreate] = useState<boolean>(false);
    const [showSettingsEdit, setShowSettingsEdit] = useState<boolean>(false);
    const [secondImg, setSecondImg] = useState<{file:any,src:string}|null>(null);
    const [generationIMGFirst,stateImgFirst,setStateImgFirst,getJobIDIMGFirst,insufficientCreditsDialogFirst] = useAiImageTypesGeneration();
    const [generationIMGSecond,stateImgSecond,setStateImgSecond,getJobIDIMGSecond,insufficientCreditsDialogSecond] = useAiImageTypesGeneration();
 
    const handleGenerationResultImageFirst = async() => {
        let img = generationIMGFirst[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setFirstImg({
            file:imgFile,
            src:img
        });
    };

    const handleGenerationResultImageSecond = async() => {
        let img = generationIMGSecond[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setSecondImg({
            file:imgFile,
            src:img
        });
    };

    const generateImageFirst = async() => {
        setStateImgFirst(null);
        const imageDimension = getImagePixelSize(valueIsAspectRatioType(inputsCreate.aspectRatio) ?  inputsCreate.aspectRatio: '1:1 (quadratic)', inputsCreate.quality ?? 'standard');
        const prompt = styleDescriptionPrompt(inputsCreate.promptPositive,inputsCreate.promptNegative, inputsCreate.style, false);
        const body: ImageGenerationBodyProps = {
            positive_prompt: prompt.positive_prompt, 
            negative_prompt: prompt.negative_prompt ? prompt.negative_prompt : undefined,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputsCreate.seed,
            steps: inputsCreate.samplingSteps,
            scheduler: inputsCreate.scheduler,
            model: 'Qwen_Image',
            cfg: inputsCreate.cfg,
            number_of_images: 1,
            sampler: inputsCreate.sampler
        };
        setStateImgFirst('send');
        let path = 'generate/image'
        try {
            getJobIDIMGFirst('image_gen',path,body);
        } catch(err){
            setStateImgFirst('error');
        };
    };
    const generateImageFromImage = async () => {
        setGenerateImgSecond(true);
        if(!firstImg){
            setStateImgSecond('error');
            return;
        }
        const file = firstImg.file;
        const base64Image = await fileToBase64(file);
        const prompt = styleDescriptionPrompt(inputsEdit.promptPositive,inputsEdit.promptNegative, inputsEdit.style, false);
        const imageDimension = await getImageDimensions(file);
        const body:ImageGenerationBodyProps = {
            image: base64Image,
            positive_prompt: prompt.positive_prompt,
            negative_prompt: prompt.negative_prompt,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputsEdit.seed,
            steps: inputsEdit.samplingSteps,
            scheduler: inputsEdit.scheduler,
            model: 'Qwen_Image_Edit`',
            cfg: inputsEdit.cfg,
            number_of_images: 1
        };
        try {
            setStateImgSecond('send');
            let path = 'generate/image-to-image-edit'
            getJobIDIMGSecond('image_gen',path,body);
        } catch(err){
            setStateImgSecond('error');
        }
    };
    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setFirstImg({
                'file':file,
                'src':imgSrc
            });
        }
    };
    const download = (imgNum:'first'|'second') => {
        let resource = '';
        if (imgNum === 'first' && firstImg){
            resource = firstImg.src;
        }
        else if (imgNum === 'second' && secondImg) {
            resource = secondImg.src;
        }
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };
    const updateCosts = async(type: 'create' | 'edit') => {
        const inputsData = type === 'create' ? {...inputsCreate} : {...inputsEdit};
        const imageDimension = type === 'create'
            ? getImagePixelSize(valueIsAspectRatioType(inputsData.aspectRatio) ?  inputsData.aspectRatio: '1:1 (quadratic)', inputsData.quality)
            : firstImg
                ? await getImageDimensions(firstImg.file)
                : {width:0,height:0}
        try{
            let dataObject: ImageCreateProps = {
                imgWidth: imageDimension.width,
                imgHeight: imageDimension.height,
                numberOfGenerations: 1,
                samplingSteps: inputsData.samplingSteps,
                cfg: inputsData.cfg,
                sampler: inputsData.sampler,
                scheduler: inputsData.scheduler,
                model:'Qwen',
                reference:  type === 'create'
                    ? null
                    : firstImg 
                        ? firstImg.file
                        : null
            }
            await ApiPayment.GetCostsForImageCreate(dataObject)
            .then((response)=>{
                if(type==='create'){
                    setCreationCostsCreate(response.costs);
                }
                else{
                    setCreationCostsEdit(response.costs);
                }
                
            })
            .catch(()=>{
                console.log('Error in calculation creation costs.');
            });
        }
        catch{
            console.log('Error in calculation creation costs.');
        }
    };

    useEffect(()=>{
        if(generationIMGFirst.length > 0){
            handleGenerationResultImageFirst();
        }
    },[generationIMGFirst]);
    useEffect(()=>{
        if(generationIMGSecond.length > 0){
            handleGenerationResultImageSecond();
        }
    },[generationIMGSecond]);
    useEffect(()=>{
        setGenerateImgSecond(false);
    },[firstImg]);
    useEffect(()=>{
        updateCosts('create');
    },[inputsCreate]);
    useEffect(()=>{
        updateCosts('edit');
    },[inputsEdit,firstImg]);

  return (
    <>
        {insufficientCreditsDialogFirst}
        {insufficientCreditsDialogSecond}
        <div className="container-fluid">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <Link 
                            to="/executions" 
                            className="btn btn-outline-secondary btn-sm ms-3"
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </Link>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                    <div className='row'>
                        <div className='col-5'>
                            {stateImgFirst 
                                ? stateImgFirst === 'send'
                                    ? <p className='h4'>{t('state.sending')}</p>
                                    : stateImgFirst === 'waiting'
                                        ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                        : stateImgFirst === 'error'
                                            ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                                : <></>
                                : <></>
                            }
                        </div>
                        <div className='col'></div>
                        <div className='col-5'>
                            {stateImgSecond 
                                ? stateImgSecond === 'send'
                                    ? <p className='h4'>{t('state.sending')}</p>
                                    : stateImgSecond === 'waiting'
                                        ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                        : stateImgSecond === 'error'
                                            ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                                : <></>
                                : <></>
                            }
                        </div>
                    </div>
                    
                    <div className='row py-2 text-center align-items-center'>
                        <div className='col-5 d-flex flex-wrap p-auto  justify-content-center'>
                            { firstImg  &&
                                <>
                                    <div className='row w-100'>
                                        <div className='col-auto'>
                                            <p className='h4 me-2'>{t('imageToEdit')}</p>
                                        </div>
                                        <div className='col-auto'>
                                            <button
                                                type="button" 
                                                className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                                                data-bs-toggle="tooltip" 
                                                data-bs-placement="top" 
                                                title={t('download.image')}
                                                >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    onClick={e =>download('first')}
                                                    size="1x"
                                                    className="pe-1"
                                                />
                                            </button>
                                        </div>
                                        
                                    </div>
                                    <div className='row mt-2'>
                                        <AuthedImage
                                            src={firstImg.src}
                                            alt={`Image: ${firstImg.file.name}`}
                                            className={'rounded d-block'}
                                            style={{height:'520px', width: 'auto', maxWidth:'100%'}}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                        <div className='col-1'> 
                            {
                                generateGif
                                ? <SVGGenerationArrowStaticBlue/>
                                : <></>
                            }

                        </div>
                        <div className='col-5 d-flex flex-wrap p-auto justify-content-center'>
                            { secondImg &&
                                <>
                                    <div className='row w-100'>
                                        <div className='col-auto'>
                                            {
                                                stateImgSecond === 'done'
                                                    ?   <button
                                                            onClick={e => setFirstImg(secondImg)}
                                                            className='border-0 bg-transparent'
                                                            title={t('editImage')}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faCircleLeft}
                                                                size='xl'
                                                                className='text-secondary'
                                                            />
                                                        </button>
                                                    : <></>
                                            }
                                        </div>
                                        <div className='col-auto'>
                                            <p className='h4 me-2'>{t('generatedImage')} </p>
                                        </div>
                                        <div className='col-auto'>
                                            <button
                                                type="button" 
                                                className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                                                data-bs-toggle="tooltip" 
                                                data-bs-placement="top" 
                                                title={t('download.image')}
                                                >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    onClick={e =>download('second')}
                                                    size="1x"
                                                    className="pe-1"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='row mt-2'>
                                        <AuthedImage
                                            src={secondImg.src}
                                            alt={`Gif`}
                                            className={'rounded d-block'}
                                            style={{height:'520px', width: 'auto', maxWidth:'100%'}}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
                    
                <div className='row p-3'>
                    <div className='col-6'>
                        <div className='row mt-2'>
                            <div className='col-auto'>
                                <label htmlFor="imgText" className="form-label fw-bold">{t('step1')}</label>
                            </div>
                            <div className='col-auto'>
                                <label htmlFor="fileuploadImage" className=' fw-bold me-2'>
                                    <FontAwesomeIcon icon={faUpload} className='me-2' />
                                    {t('uploadImage')}
                                </label>
                                <input
                                id = 'fileuploadImage'
                                type= 'file'
                                accept='image/png,image/jpg,image/jpeg'
                                onChange={event => uploadFile(event)}
                                hidden
                                />
                                {
                                    firstImg
                                    ? firstImg.file.name
                                    : <></>
                                }
                            </div>
                    
                        </div>
                        <div className='row mt-2'>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="promptPositive"
                                name='promptPositive'
                                rows={5}
                                value={inputsCreate.promptPositive}
                                onChange={(e) => setInputsCreate(prev => ({...prev, 'promptPositive': e.target.value}))}
                                placeholder={t('placeholder.describePositive')}
                            />
                            </div>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="promptNegative"
                                name='promptNegative'
                                rows={5}
                                value={inputsCreate.promptNegative}
                                onChange={(e) => setInputsCreate(prev => ({...prev, 'promptNegative': e.target.value}))}
                                placeholder={t('placeholder.describeNegative')}
                            />
                            </div>
                            <div className='col-auto  d-flex flex-column justify-content-center text-start'>
                                <button
                                    className="btn btn-secondary"
                                    title={t('generateImage')}
                                    onClick={() => generateImageFirst()}
                                >
                                    <FontAwesomeIcon
                                        icon={faWandMagicSparkles}
                                        className='bg-transparent me-2'
                                    />
                                     {t('coins')} {creationCostsCreate ?? '—'}
                                </button>
                            </div>
                        </div>
                        <div className='row mb-2'>
                            <button
                                className="btn btn-transparent m-0 p-0 btn-lg text-start"
                                onClick={() => setShowSettingsCreate(!showSettingsCreate)}
                            >
                                <FontAwesomeIcon
                                    icon={showSettingsCreate ? faChevronDown : faChevronRight}
                                    className="me-2"
                                />
                                {t('promptSettings')}
                            </button>
                        </div>
                        {
                            showSettingsCreate
                            ?   <>
                                    <div className='row m-0 ps-3 ms-1'>
                                        <p className='test-small text-secondary m-0'>{t('basic')}</p>
                                    </div>
                                    <div className='row ps-3 m-0 ms-1'>
                                        <div className='col-4'>
                                            <div className="mb-1">{t('aspectRatio')}</div>
                                            <EffectThemeDropdown
                                                className="w-100 mb-2"
                                            >
                                                <EffectThemeDropdown.Toggle
                                                    id="dropdown-ai-model"
                                                    className="w-100"
                                                >
                                                    {inputsCreate.aspectRatio}
                                                </EffectThemeDropdown.Toggle>
        
                                                <EffectThemeDropdown.Menu 
                                                    className="p-1 w-100"
                                                    maxHeight={420}
                                                    style={{ zIndex: 999999 }}
                                                >
                                                    {aspectRatioList.map((item, index) => (
                                                    <EffectThemeDropdown.Item
                                                        key={index}
                                                        onClick={()=> setInputsCreate(prev => ({...prev,'aspectRatio': item}))}
                                                        active={item === inputsCreate.aspectRatio}
                                                    >
                                                        {item}
                                                    </EffectThemeDropdown.Item>
                                                    ))}
                                                </EffectThemeDropdown.Menu>
                                            </EffectThemeDropdown>
                                        </div>
                                        <div className='col-6'>
                                            <div className="mb-1">{t('style')}</div>
                                            <EffectThemeDropdown.SearchFilter
                                                id="dropdown-style-model"
                                                className="w-100 mb-2"
                                                selectedLabel={stylesList.filter(x => x.item === inputsCreate.style).length > 0 ? stylesList.filter(x => x.item === inputsCreate.style)[0].name : ''}
                                                items={stylesList}
                                                selectedItem={stylesList.find(x => x.item === inputsCreate.style) ?? null}
                                                onSelect={(x) => {
                                                    setInputsCreate(prev => ({...prev, "style": x.item}))
                                                }}
                                                getItemKey={(x) => x.item}
                                                getItemSearchText={(x) =>`${x.name ?? ""} ${x.item} ${(x.category ?? []).join(" ")}`}
                                                renderItem={renderStyleDropdownItem}
                                                filterGroups={styleFilterGroups}
                                                searchPlaceholder={t('placeholder.searchStyle')}
                                                resetKey={"create-image"}
                                                maxHeight={420}
                                            />
                                        </div>
                                        
                                    </div>
                                    <div className='row m-0 ps-3 ms-1'>
                                        <p className='test-small text-secondary m-0'>{t('advanced')}</p>
                                    </div>
                                    <div className='row ps-3 m-0 ms-1'>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0"> {t('samplingSteps')}</div>
                                                <div className="col">{inputsCreate.samplingSteps}</div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SamplingStepsInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeRange
                                                min={1}
                                                max={40}
                                                step={1}
                                                name="samplingSteps"
                                                value={inputsCreate.samplingSteps}
                                                onChange={val => setInputsCreate(prev => ({...prev,'samplingSteps': val}))}
                                                minDescription={t('range.fast')}
                                                maxDescription={t('range.slow')}
                                                className="mb-2"
                                            />
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0 align-items-center">
                                                <div className="col-auto mb-1 ps-0">
                                                    {t('cfgScale')}
                                                </div>
                                                <div className="col text-end">
                                                    {inputsCreate.cfg}
                                                </div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <CfgInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeRange
                                                min={0}
                                                max={20}
                                                step={0.1}
                                                name="cfg"
                                                value={inputsCreate.cfg}
                                                onChange={val => setInputsCreate(prev => ({...prev,'cfg': val}))}
                                                minDescription={t('range.creative')}
                                                maxDescription={t('range.exact')}
                                                className="mb-2"
                                            />
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('sampler')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SamplerInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeDropdown className="w-100 mb-2">
                                                <EffectThemeDropdown.Toggle
                                                    id="dropdown-sampler"
                                                    className="w-100"
                                                >
                                                    {inputsCreate.sampler}
                                                </EffectThemeDropdown.Toggle>
        
                                                <EffectThemeDropdown.Menu className="p-1 w-100">
                                                    {samplerList.map((item, index) => (
                                                    <EffectThemeDropdown.Item
                                                        key={index}
                                                        onClick={()=> setInputsCreate(prev => ({...prev, 'sampler': item}))}
                                                        active={item === inputsCreate.sampler}
                                                    >
                                                        {item}
                                                    </EffectThemeDropdown.Item>
                                                    ))}
                                                </EffectThemeDropdown.Menu>
                                            </EffectThemeDropdown>
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('scheduler')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SchedulerInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeDropdown className="w-100 mb-2">
                                                <EffectThemeDropdown.Toggle
                                                    id="dropdown-scheduler"
                                                    className="w-100"
                                                >
                                                    {inputsCreate.scheduler}
                                                </EffectThemeDropdown.Toggle>
        
                                                <EffectThemeDropdown.Menu className="p-1 w-100">
                                                    {schedulerList.map((item, index) => (
                                                    <EffectThemeDropdown.Item
                                                        key={index}
                                                        onClick={()=> setInputsCreate(prev => ({...prev,'scheduler': item}))}
                                                        active={item === inputsCreate.scheduler}
                                                    >
                                                        {item}
                                                    </EffectThemeDropdown.Item>
                                                    ))}
                                                </EffectThemeDropdown.Menu>
                                            </EffectThemeDropdown>
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('quality')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <QualityInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeDropdown className="w-100 mb-2">
                                                <EffectThemeDropdown.Toggle
                                                    id="dropdown-quality"
                                                    className="w-100"
                                                >
                                                    {inputsCreate.quality}
                                                </EffectThemeDropdown.Toggle>
        
                                                <EffectThemeDropdown.Menu className="p-1 w-100">
                                                    {qualityList.map((item, index) => (
                                                    <EffectThemeDropdown.Item
                                                        key={index}
                                                        onClick={()=> setInputsCreate(prev => ({...prev,'quality': item}))}
                                                        active={item === inputsCreate.quality}
                                                    >
                                                        {item}
                                                    </EffectThemeDropdown.Item>
                                                    ))}
                                                </EffectThemeDropdown.Menu>
                                            </EffectThemeDropdown>
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('seed')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SeedInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <div className="row m-0 p-0">
                                                <div className="col ps-0">
                                                    <EffectThemeInput
                                                        type="number"
                                                        name="seed"
                                                        value={inputsCreate.seed}
                                                        min={-1}
                                                        max={999999}
                                                        step={1}
                                                        inputClassName="mb-2"
                                                        onValueChange={val => {
                                                            setInputsCreate(prev => ({ ...prev, ['seed']: Number(val) }));
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-auto">
                                                    <button
                                                        className="btn btn-transparent m-0 p-0"
                                                        title={t('getRandomSeed')}
                                                        onClick={() => setInputsCreate(p => ({ ...p, seed: handleGetRandomNumber() }))}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faDice}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-4 my-auto'>
                                            <div className="ps-0">
                                                <Form.Check
                                                    label={t('changeSeedAuto')}
                                                    type="checkbox"
                                                    title={t('changeSeedAutoHint')}
                                                    checked={seedRandomCreateActive}
                                                    onChange={() => {
                                                        setSeedRandomCreateActive(prev => !prev);
                                                    }}
                                                    style={{ fontSize: 16 }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            :   <></>
                        }
                    </div>
                    <div className='col-6'>
                        <div className='row mt-2'>
                            <label htmlFor="animationText" className="form-label fw-bold">{t('step2')}</label>
                        </div>
                        <div className='row mt-2'>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="promptPositive"
                                name='promptPositive'
                                rows={5}
                                value={inputsEdit.promptPositive}
                                onChange={(e) => setInputsEdit(prev => ({...prev,'promptPositive':e.target.value}))}
                                placeholder={t('placeholder.describePositive')}
                            />
                            </div>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="promptNegative"
                                name='promptNegative'
                                rows={5}
                                value={inputsEdit.promptNegative}
                                onChange={(e) => setInputsEdit(prev => ({...prev,'promptNegative':e.target.value}))}
                                placeholder={t('placeholder.describeNegative')}
                            />
                            </div>
                            <div className='col-auto d-flex flex-column justify-content-center text-start'>
                                <button
                                    className="btn btn-secondary"
                                    title={firstImg===null? t('uploadOrGenerateFirst') : t('generateGif')}
                                    onClick={() => generateImageFromImage()}
                                    disabled={firstImg===null}
                                >
                                    <FontAwesomeIcon
                                        icon={faWandMagicSparkles}
                                        className='bg-transparent me-2'
                                    />
                                     {t('coins')} {creationCostsEdit ?? '—'}
                                </button>
                            </div>
                        </div>
                        <div className='row mb-2'>
                            <button
                                className="btn btn-transparent m-0 p-0 btn-lg text-start"
                                onClick={() => setShowSettingsEdit(!showSettingsEdit)}
                            >
                                <FontAwesomeIcon
                                    icon={showSettingsEdit ? faChevronDown : faChevronRight}
                                    className="me-2"
                                />
                                {t('promptSettings')}
                            </button>
                        </div>
                        {
                            showSettingsEdit
                            ?   <>
                                    <div className='row m-0 ps-3 ms-1'>
                                        <p className='test-small text-secondary m-0'>{t('basic')}</p>
                                    </div>
                                    <div className='row ps-3 m-0 ms-1'>
                                        <div className='col-6'>
                                            <div className="mb-1">{t('style')}</div>
                                            <EffectThemeDropdown.SearchFilter
                                                id="dropdown-style-model"
                                                className="w-100 mb-2"
                                                selectedLabel={stylesList.filter(x => x.item === inputsEdit.style).length > 0 ? stylesList.filter(x => x.item === inputsEdit.style)[0].name : ''}
                                                items={stylesList}
                                                selectedItem={stylesList.find(x => x.item === inputsEdit.style) ?? null}
                                                onSelect={(x) => {
                                                    setInputsEdit(prev => ({...prev, "style": x.item}))
                                                }}
                                                getItemKey={(x) => x.item}
                                                getItemSearchText={(x) =>`${x.name ?? ""} ${x.item} ${(x.category ?? []).join(" ")}`}
                                                renderItem={renderStyleDropdownItem}
                                                filterGroups={styleFilterGroups}
                                                searchPlaceholder={t('placeholder.searchStyle')}
                                                resetKey={"edit-image"}
                                                maxHeight={420}
                                            />
                                        </div>
                                        
                                    </div>
                                    <div className='row m-0 ps-3 ms-1'>
                                        <p className='test-small text-secondary m-0'>{t('advanced')}</p>
                                    </div>
                                    <div className='row ps-3 m-0 ms-1'>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0"> {t('samplingSteps')}</div>
                                                <div className="col">{inputsEdit.samplingSteps}</div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SamplingStepsInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeRange
                                                min={1}
                                                max={40}
                                                step={1}
                                                name="samplingSteps"
                                                value={inputsEdit.samplingSteps}
                                                onChange={val => setInputsEdit(prev => ({...prev,'samplingSteps': val}))}
                                                minDescription={t('range.fast')}
                                                maxDescription={t('range.slow')}
                                                className="mb-2"
                                            />
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0 align-items-center">
                                                <div className="col-auto mb-1 ps-0">
                                                    {t('cfgScale')}
                                                </div>
                                                <div className="col text-end">
                                                    {inputsEdit.cfg}
                                                </div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <CfgInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeRange
                                                min={0}
                                                max={20}
                                                step={0.1}
                                                name="cfg"
                                                value={inputsEdit.cfg}
                                                onChange={val => setInputsEdit(prev => ({...prev,'cfg': val}))}
                                                minDescription={t('range.creative')}
                                                maxDescription={t('range.exact')}
                                                className="mb-2"
                                            />
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('sampler')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SamplerInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeDropdown className="w-100 mb-2">
                                                <EffectThemeDropdown.Toggle
                                                    id="dropdown-sampler"
                                                    className="w-100"
                                                >
                                                    {inputsEdit.sampler}
                                                </EffectThemeDropdown.Toggle>
        
                                                <EffectThemeDropdown.Menu className="p-1 w-100">
                                                    {samplerList.map((item, index) => (
                                                    <EffectThemeDropdown.Item
                                                        key={index}
                                                        onClick={()=> setInputsEdit(prev => ({...prev, 'sampler': item}))}
                                                        active={item === inputsEdit.sampler}
                                                    >
                                                        {item}
                                                    </EffectThemeDropdown.Item>
                                                    ))}
                                                </EffectThemeDropdown.Menu>
                                            </EffectThemeDropdown>
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('scheduler')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SchedulerInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <EffectThemeDropdown className="w-100 mb-2">
                                                <EffectThemeDropdown.Toggle
                                                    id="dropdown-scheduler"
                                                    className="w-100"
                                                >
                                                    {inputsEdit.scheduler}
                                                </EffectThemeDropdown.Toggle>
        
                                                <EffectThemeDropdown.Menu className="p-1 w-100">
                                                    {schedulerList.map((item, index) => (
                                                    <EffectThemeDropdown.Item
                                                        key={index}
                                                        onClick={()=> setInputsEdit(prev => ({...prev,'scheduler': item}))}
                                                        active={item === inputsEdit.scheduler}
                                                    >
                                                        {item}
                                                    </EffectThemeDropdown.Item>
                                                    ))}
                                                </EffectThemeDropdown.Menu>
                                            </EffectThemeDropdown>
                                        </div>
                                        <div className='col-3'>
                                            <div className="row m-0 p-0">
                                                <div className="col-auto mb-1 ps-0">{t('seed')}</div>
                                                <div className="col"></div>
                                                <div className="col-auto mb-1 p-0">
                                                    <InfoPopoverButton
                                                        width={320}
                                                        placement="right"
                                                    >
                                                        <SeedInfoElement/>
                                                    </InfoPopoverButton>
                                                </div>
                                            </div>
                                            <div className="row m-0 p-0">
                                                <div className="col ps-0">
                                                    <EffectThemeInput
                                                        type="number"
                                                        name="seed"
                                                        value={inputsEdit.seed}
                                                        min={-1}
                                                        max={999999}
                                                        step={1}
                                                        inputClassName="mb-2"
                                                        onValueChange={val => {
                                                            setInputsEdit(prev => ({ ...prev, ['seed']: Number(val) }));
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-auto">
                                                    <button
                                                        className="btn btn-transparent m-0 p-0"
                                                        title={t('getRandomSeed')}
                                                        onClick={() => setInputsEdit(p => ({ ...p, seed: handleGetRandomNumber() }))}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faDice}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-4 my-auto'>
                                            <div className="ps-0">
                                                <Form.Check
                                                    label={t('changeSeedAuto')}
                                                    type="checkbox"
                                                    title={t('changeSeedAutoHint')}
                                                    checked={seedRandomEditActive}
                                                    onChange={() => {
                                                        setSeedRandomEditActive(prev => !prev);
                                                    }}
                                                    style={{ fontSize: 16 }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            :   <></>
                        }
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default ImageToImageEdit;